import React from 'react';
import { Box, Typography, Button, CircularProgress, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { FaArrowLeft } from 'react-icons/fa';
import BusinessNameStep from './BusinessNameStep';
import WebsiteStep from './WebsiteStep';
import AddressStep from './AddressStep';
import ContactStep from './ContactStep';
import BusinessDetailsStep from './BusinessDetailsStep';
import AIReceptionistReasonsStep from './AIReceptionistReasonsStep';
import PaymentStep from './PaymentStep';
import PaymentConfirmationStep from './PaymentConfirmationStep';
import ExpectRoiIcon from '@/assets/icons/ExpectRoiIcon';
import UserIcon from '@/assets/icons/UserIcon';
import DollarIcon from '@/assets/icons/DollarIcon';
import LockIcon from '@/assets/icons/LockIcon';
import ZeroDisruptionIcon from '@/assets/icons/ZeroDisruptionIcon';
import TrustedIcon from '@/assets/icons/TrustedIcon';

import { Control } from 'react-hook-form';

interface RightPanelProps {
  formStep: number;
  control: Control<any>;
  setValue: any;
  watch: any;
  handleNext: () => void | Promise<void>;
  handleBack: () => void | Promise<void>;
  handlePayment: () => void | Promise<void>;
  isSubmittingFinal?: boolean;
  isSubmittingPayment?: boolean;
  onboardingData?: any;
  billingEnabled?: boolean;
  organizationData?: any;
  disabledFields?: {
    primary_contact_phone?: boolean;
    primary_contact_email?: boolean;
  };
}

const MobileIntro: React.FC<{ organizationData: any; handleNext: () => void }> = ({ organizationData, handleNext }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: { xs: '100dvh', md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 2,
        py: 6,
        gap: 2.5,
        background: 'linear-gradient(154deg, #77B2DC 0%, #FFFFFF 49.07%, #D9F66C 99.08%)',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 20
      }}
    >
      {organizationData?.logo_url && (
        <Box
          component='img'
          src={organizationData.logo_url}
          alt={organizationData?.name}
          sx={{ height: 50, width: 'auto', objectFit: 'contain' }}
        />
      )}

      <Box>
        <Typography variant='h4' fontWeight={600} fontSize={20}>
          You've made the right call.
        </Typography>
        <Typography lineHeight='normal' variant='h4' fontWeight={600} fontSize={20}>
          Let’s build your <span style={{ color: theme.palette.primary.main }}>AI receptionist. </span>
        </Typography>
      </Box>

      <Typography color='text.secondary' sx={{ maxWidth: '400px' }}>
        A few quick steps and your AI agent will be ready to handle calls, book appointments, and delight your customers
        — 24/7.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: '350px' }}>
        {[
          { text: 'Expect 10x ROI from day one', icon: <ExpectRoiIcon /> },
          { text: 'We map your receptionist workflow for you', icon: <UserIcon /> },
          { text: 'Monthly subscription starts once agent is live', icon: <DollarIcon /> },
          { text: 'Cancel anytime — no lock-in contracts', icon: <LockIcon /> },
          { text: 'Zero disruption — nothing changes', icon: <ZeroDisruptionIcon /> }
        ].map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left' }}>
            <Box sx={{ flexShrink: 0, color: theme.palette.primary.main, '& svg': { width: 32, height: 32 } }}>
              {item.icon}
            </Box>
            <Typography fontWeight={400} fontSize='14px'>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ width: '100%', maxWidth: '350px', mt: 2 }}>
        <Button variant='contained' fullWidth size='large' onClick={handleNext}>
          Get Started
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3 }}>
          <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>
            <TrustedIcon />
          </Box>
          <Typography variant='caption' color='text.secondary' fontWeight={500}>
            Trusted by 500+ businesses across North America
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const RightPanel: React.FC<RightPanelProps> = ({
  formStep,
  control,
  setValue,
  watch,
  handleNext,
  handleBack,
  handlePayment,
  isSubmittingFinal = false,
  isSubmittingPayment = false,
  onboardingData,
  billingEnabled = true,
  organizationData,
  disabledFields
}) => {
  const theme = useTheme();
  const totalSteps = billingEnabled ? 7 : 6;
  const isLastStep = formStep === (billingEnabled ? 7 : 6);
  const isPaymentStep = formStep === 6 && billingEnabled;
  const isConfirmationStep = formStep === 7;

  const onNextClick = isPaymentStep ? handlePayment : handleNext;

  return (
    <Grid
      size={{ xs: 12, md: 6 }}
      sx={{
        height: { xs: '100dvh', md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* ── Mobile Header (hidden on md+) ── */}
      <Box
        sx={{
          display: { xs: formStep === -1 ? 'none' : 'flex', md: 'none' },
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 2.5,
          py: 1.5,
          alignItems: 'center',
          gap: 1.5
        }}
      >
        {organizationData?.logo_url && (
          <Box
            component='img'
            src={organizationData.logo_url}
            alt={organizationData?.name}
            sx={{ height: 32, width: 'auto', objectFit: 'contain' }}
          />
        )}
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.2 }}>
            {formStep === -1 ? 'Welcome to Kickcall' : 'Stage 1: Business Onboarding'}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.3 }}>
            {formStep === -1 ? 'Getting started' : 'Setting up your account'}
          </Typography>
        </Box>
      </Box>

      {/* ── Scrollable Content Area ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: formStep === -1 ? 0 : 2, md: '120px' },
          py: { xs: formStep === -1 ? 0 : 2, md: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            width: '100%',
            my: { xs: 0, md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {/* Desktop HEADER (step badge + back) */}
          {formStep !== 7 && formStep !== -1 && (
            <Box
              width='100%'
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={1.5}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              {formStep > 0 && (
                <Typography
                  display='flex'
                  gap={1}
                  alignItems='center'
                  onClick={handleBack}
                  sx={{ cursor: 'pointer', fontSize: '12px', color: 'text.secondary' }}
                >
                  <FaArrowLeft /> Back
                </Typography>
              )}

              <Box
                sx={{
                  background: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  px: 2,
                  py: 0.5,
                  ml: 'auto',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                Step {formStep + 1}/{totalSteps}
              </Box>
            </Box>
          )}

          {/* FORM STEPS */}
          {formStep === -1 && <MobileIntro organizationData={organizationData} handleNext={handleNext} />}
          {formStep === 0 && (
            <ContactStep
              control={control}
              onNext={handleNext}
              isSubmitting={isSubmittingFinal}
              disabledFields={disabledFields}
            />
          )}
          {formStep === 1 && (
            <BusinessNameStep
              control={control}
              setValue={setValue}
              onNext={handleNext}
              isSubmitting={isSubmittingFinal}
            />
          )}
          {formStep === 2 && (
            <WebsiteStep control={control} setValue={setValue} onNext={handleNext} isSubmitting={isSubmittingFinal} />
          )}
          {formStep === 3 && <AddressStep control={control} onNext={handleNext} isSubmitting={isSubmittingFinal} />}
          {formStep === 4 && (
            <BusinessDetailsStep
              control={control}
              setValue={setValue}
              watch={watch}
              onNext={handleNext}
              isSubmitting={isSubmittingFinal}
            />
          )}
          {formStep === 5 && (
            <AIReceptionistReasonsStep
              control={control}
              onNext={handleNext}
              isSubmitting={isSubmittingFinal}
              billingEnabled={billingEnabled}
            />
          )}
          {formStep === 6 && (
            <PaymentStep onNext={handlePayment} isSubmitting={isSubmittingPayment} onboardingData={onboardingData} />
          )}
          {formStep === 7 && <PaymentConfirmationStep onNext={handleNext} onboardingData={onboardingData} />}
        </Box>
      </Box>

      {/* ── Mobile Footer (hidden on md+) ── */}
      {!isConfirmationStep && (
        <Box
          sx={{
            display: { xs: formStep === -1 ? 'none' : 'flex', md: 'none' },
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2.5,
            py: 1.5,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          {/* Back */}
          <Button
            variant='outlined'
            size='small'
            color='secondary'
            disabled={formStep <= -1}
            onClick={handleBack}
            sx={{
              minWidth: 80,
              visibility: formStep <= -1 ? 'hidden' : 'visible'
            }}
          >
            Back
          </Button>

          {/* Step Indicator */}
          <Typography
            sx={{
              fontSize: '13px',
              color: 'text.secondary',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            {formStep === -1 ? 'Intro' : `Step ${formStep + 1} of ${totalSteps}`}
          </Typography>

          {/* Next / Pay */}
          <Button
            variant='contained'
            size='small'
            onClick={onNextClick}
            disabled={isSubmittingFinal || isSubmittingPayment}
            sx={{ minWidth: 80, display: isPaymentStep ? 'none' : 'inline-flex' }}
          >
            {isSubmittingFinal || isSubmittingPayment ? (
              <CircularProgress size={16} color='inherit' />
            ) : isPaymentStep ? (
              'Pay Now'
            ) : isLastStep ? (
              'Finish'
            ) : (
              'Continue'
            )}
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export default RightPanel;
